import { Dispatch, SetStateAction } from 'react';
import i18next from 'i18next';
import { Link } from 'react-router';
import ColumnHeader from 'Components/UI/Table/ColumnHeader';
import { Icon } from 'Components/UI';
import { Badge, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import FilterDate from '../FilterData';
import { datetimeToString, formatDateByTime } from 'Utils/DateUtils';
import { getFilters, getOrderTitle } from './Notification.helpers';
import {
  NotificationActionUnion,
  NotificationActions
} from 'Store/Notification/Notification.actions';
import {
  INotificationFilters,
  IFilterDateOption,
  IFilterObraOption,
  ISelectedObraFilter,
  INotificationOrder,
  INotificationOrderKey
} from 'Store/Notification/interfaces/INotificationState';
import { INotificationData } from 'Data/interfaces/Notification/INotificationResponse';
import FilterModal from 'Pages/ListagemArquivos/Modals/FilterModal';
import { GetAvatar, getUserAvatarOrThumb } from 'Utils/generateThumbnail';
import { iconFileByFilename } from 'Utils/Icons';
import { NotificationActionEnum } from 'Data/enums/Notification';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import FilterObra from '../FilterObra';
import Ellipsis from 'Components/UI/Ellipsis';
import dayjs from 'dayjs';
import styles from './Notification.module.scss';

interface IColumns {
  filters: INotificationFilters;
  notificationList: INotificationData[];
  dates: IFilterDateOption[];
  subtractDays: number;
  csIdsFilterObra: number[];
  obrasMap: Record<number, IObraResponse>;
  obrasFilters: IFilterObraOption[];
  selectedObraFilter: ISelectedObraFilter;
  order: INotificationOrder;
  setSelectedObraFilter: Dispatch<SetStateAction<ISelectedObraFilter>>;
  onOrder: (key: INotificationOrderKey) => void;
  dispatch: Dispatch<NotificationActionUnion>;
}

export const tableColumns: (input: IColumns) => Array<ColumnProps<any>> = ({
  notificationList,
  filters,
  dates,
  subtractDays,
  csIdsFilterObra,
  obrasMap,
  obrasFilters,
  selectedObraFilter,
  order,
  setSelectedObraFilter,
  onOrder,
  dispatch,
}) => [
    {
      dataIndex: 'ActionName',
      width: 'auto',
      title: (
        <ColumnHeader
          title={i18next.t('notification:columns.notifications')}
          showFilter={() => dispatch(NotificationActions.setShowNotification(true))}
          sorter={() => {
            onOrder('notifications');
          }}
          orderTooltip={getOrderTitle(order.notifications, 'notifications')}
        />
      ),
      key: 'ActionName',
      className: styles['notificationHeader'],
      onFilter: (value: any, record: any) => {
        return record.ActionName.toLowerCase() === value;
      },
      onFilterDropdownVisibleChange: (visible) => dispatch(NotificationActions.setShowNotification(visible)),
      filterDropdownVisible: filters.showNotification,
      filterDropdown: (propsFilters: any) => {
        const showDataFilters = getFilters(
          notificationList,
          'ActionName',
          (x: string) => x || ''
        );
        return (
          <FilterModal
            propsFilter={propsFilters}
            tipoOptions={showDataFilters}
            showFilter={() => dispatch(NotificationActions.setShowNotification(false))}
            placeholder={i18next.t('notification:columns.searchAction')}
          />
        );
      },
      render: (_: any, {
        ActionName,
        hasRead,
        Description,
        ItemLocationUrl,
        ItemName,
        Action,
        ItemUrl
      }: INotificationData) => {
        let itemNameAux = ItemName;
        if (
          Action === NotificationActionEnum.ArquivoMovido &&
          Description?.length > 0 &&
          Description.lastIndexOf('/') > -1
        ) {
          const folderName = Description.substring(Description.lastIndexOf('/') + 1);
          itemNameAux += ` para "${folderName}"`;
        }
        return (
          <div className={styles['notificationRow']}>
            <Icon
              color="cinzaPadrao"
              icon={iconFileByFilename(ItemName)}
              customSize={18}
            />
            <Link to={ItemUrl}>
              <Ellipsis
                phrase={ActionName}
                maxLength={35}
                className={styles['itemName']}
              />
            </Link>
            <Link to={ItemLocationUrl}>
              <span style={{ display: 'flex' }}>
                <Icon
                  color="cinzaPadrao"
                  icon={'arquivar'}
                  size='xxs'
                  style={{ marginTop: 5 }}
                />
                <Ellipsis
                  phrase={itemNameAux}
                  maxLength={ActionName?.length > 27 ? 37 : 45}
                  className={styles['itemName']}
                />
              </span>
            </Link>
            {!hasRead ? (
              <Badge dot color='#FF3D57' className={styles['badge']} />
            ) : <></>}
          </div>
        )
      },
    },
    {
      dataIndex: 'ConstructionSiteFk',
      width: 250,
      title: (
        <ColumnHeader
          title={i18next.t('common:work')}
          showFilter={() => dispatch(NotificationActions.setShowFilterObra(true))}
          badgeCount={selectedObraFilter.count}
          onClearFilter={() => {
            dispatch(NotificationActions.setShowFilterObra(true));
            setSelectedObraFilter({
              count: 0,
              clear: true
            });
          }}
          sorter={() => {
            onOrder('obra');
          }}
          orderTooltip={getOrderTitle(order.obra, 'obra')}
        />
      ),
      key: 'ConstructionSiteFk',
      className: styles['userHeader'],
      onFilter: (value: any, record: any) => {
        return record.ConstructionSiteFk === value;
      },
      onFilterDropdownVisibleChange: (visible) => dispatch(NotificationActions.setShowFilterObra(visible)),
      filterDropdownVisible: filters.showObra,
      filterDropdown: (propsFilters: any) => {
        if (
          !selectedObraFilter.clear &&
          ((propsFilters?.selectedKeys?.length > 0 && propsFilters?.selectedKeys?.length !== selectedObraFilter.count) ||
            ((!propsFilters || propsFilters?.selectedKeys?.length === 0) && selectedObraFilter.count > 0))
        ) {
          setSelectedObraFilter(prev => ({
            ...prev,
            count: propsFilters?.selectedKeys?.length || 0
          }));
        }
        return (
          <FilterObra
            minWidth='200px'
            obras={obrasFilters}
            defaultSelectedKeys={csIdsFilterObra}
            propsFilter={propsFilters}
            selectedObraFilter={selectedObraFilter}
            setSelectedObraFilter={setSelectedObraFilter}
            showFilter={(visible: boolean) => dispatch(NotificationActions.setShowFilterObra(visible))}
          />
        );
      },
      render: (_: any, {
        ConstructionSiteFk
      }: Pick<INotificationData, 'ConstructionSiteFk'>) => {
        const obra = obrasMap[ConstructionSiteFk];
        return (
          <div className={styles['userRow']}>
            <GetAvatar
              name={obra?.Name}
              thumbSrc={obra?.ThumbnailImageUrl}
              src={obra?.OriginalImageUrl}
              shape='circle'
              size='small'
            />
            <Tooltip title={obra?.Name}>
              <p>{obra?.Name}</p>
            </Tooltip>
          </div>
        )
      },
    },
    {
      dataIndex: 'UserName',
      width: 250,
      title: (
        <ColumnHeader
          title={i18next.t('common:user')}
          showFilter={() => dispatch(NotificationActions.setShowNotificationUsers(true))}
          sorter={() => {
            onOrder('users');
          }}
          orderTooltip={getOrderTitle(order.users, 'users')}
        />
      ),
      key: 'UserName',
      className: styles['userHeader'],
      onFilter: (value: any, record: any) => record.UserName.toLowerCase() === value,
      onFilterDropdownVisibleChange: (visible) => dispatch(NotificationActions.setShowNotificationUsers(visible)),
      filterDropdownVisible: filters.showUsers,
      filterDropdown: (propsFilters: any) => {
        const showUsersFilters = getFilters(
          notificationList,
          'UserName',
          undefined,
          (index) => index !== '-',
          (updateIndex) => {
            return (
              <div className={styles['filterModificadoPor']}>
                <GetAvatar name={updateIndex} shape='circle' size='small' />
                <Ellipsis
                  phrase={updateIndex}
                  maxLength={29}
                  className={styles['tipoOptionName']}
                />
              </div>
            );
          }
        );
        return <FilterModal
          width='200px'
          propsFilter={propsFilters}
          tipoOptions={showUsersFilters}
          showFilter={(visible: boolean) => dispatch(NotificationActions.setShowNotificationUsers(visible))}
          placeholder={i18next.t('notification:columns.searchUser')}
        />;
      },
      render: (_: any, {
        UserName,
        AvatarFileNameUser
      }: Pick<INotificationData, 'AvatarFileNameUser' | 'UserName'>) => {
        return (
          <div className={styles['userRow']}>
            {getUserAvatarOrThumb(AvatarFileNameUser, UserName)}
            <Tooltip title={UserName}>
              <p>{UserName}</p>
            </Tooltip>
          </div>
        )
      },
    },
    {
      dataIndex: 'DateCreateUtc',
      width: 200,
      title: (
        <ColumnHeader
          title={i18next.t('common:date')}
          showFilter={() => dispatch(NotificationActions.setShowNotificationData(true))}
          sorter={() => {
            onOrder('data');
          }}
          orderTooltip={getOrderTitle(order.data, 'data')}
        />
      ),
      key: 'DateCreateUtc',
      className: styles['dateHeader'],
      onFilter: (_, record: any) => {
        const now = dayjs();
        const date = now.subtract(subtractDays, 'days');
        const formatedDate = dayjs(record.DateCreateUtc);

        if (subtractDays === 0) {
          const today = now.startOf('day');
          const tomorrow = today.add(1, 'day');
          return formatedDate.isBefore(tomorrow) && today.isBefore(formatedDate);
        }

        return date.isBefore(formatedDate) || date.isSame(formatedDate, 'day');
      },
      onFilterDropdownVisibleChange: (visible) => dispatch(NotificationActions.setShowNotificationData(visible)),
      filterDropdownVisible: filters.showData,
      filterDropdown: (propsFilter: any) => {
        return <FilterDate
          width='200px'
          dates={dates}
          propsFilter={propsFilter}
        />;
      },
      render: (DateCreateUtc: string) => {
        const convertedDate = new Date(DateCreateUtc);
        return (
          <Tooltip title={datetimeToString(convertedDate)}>
            <div className={styles['dateRow']}>
              <p>{formatDateByTime(convertedDate)}</p>
            </div>
          </Tooltip>
        )
      },
    }
  ];
